import { useState, useContext } from "react";
import {
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Box,
  Checkbox,
  UnorderedList,
  ListItem,
  useColorMode,
  useToast,
  SimpleGrid,
} from "@chakra-ui/react";
import Image from "next/image";

// Types
import { WalletMenuOption } from "types/common";
import Props from "./WalletMenuProps.type";

// Components
import Alert from "components/Alert";

// Context
import { StakeContext } from "providers/StakeProvider";

// Constants
import { TERMS_URL } from "constants/constants";
import { ACCEPT_TOS, CLICK_TNC_LINK } from "constants/analytics";
import { walletIconMapping } from "constants/walletMenuOptions";

// Utils
import { openLinkInNewTab } from "utils/common";
import { isZerion } from "utils/connectors";

export const WalletConnectModal = ({
  isOpen,
  walletMenuOptions: walletOptions,
}: Props) => {
  const { handleActivate, closeModal, track } = useContext(StakeContext);

  const { colorMode } = useColorMode();

  const [isChecked, setChecked] = useState(false);
  const { ethereum, phantom } = window as any;
  const walletMenuOptions = ethereum
    ? walletOptions.filter((wo: WalletMenuOption) => {
        if (wo.key === "injected" && ethereum?.isKuCoinWallet) {
          return false;
        }

        if (wo.key === "phantom" && phantom && !phantom?.ethereum) {
          return false;
        }

        return true;
      })
    : walletOptions;

  const toast = useToast();
  const toggle = () => {
    track(ACCEPT_TOS);
    setChecked((prev) => !prev);
  };

  const handleClose = () => {
    setChecked(false);
    closeModal();
  };

  const handleClick = (key: string) => {
    if (
      key === "kuwallet" &&
      !ethereum?.isKuCoin &&
      !ethereum?.isKuCoinWallet
    ) {
      toast({
        description:
          "Please visit Stader through the Kucoin Mobile Wallet browser or please install Kucoin browser extension",
        status: "error",
        duration: 2000,
        position: "top",
        isClosable: true,
      });
      return;
    }
    handleActivate(key, handleClose);
  };

  const handleTNCClick = () => {
    track(CLICK_TNC_LINK);
    openLinkInNewTab(TERMS_URL);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent w="430px" bg="brand.backgroundLight" m="1rem">
        <ModalHeader fontSize="1rem">Select Wallet</ModalHeader>
        <ModalCloseButton
          _focus={{
            boxShadow: "none",
          }}
        />
        <ModalBody>
          <Box
            bg="brand.textBg"
            p="1rem"
            marginBottom="1rem"
            borderRadius="0.5rem"
          >
            <Checkbox onChange={toggle} checked={isChecked}>
              <Text fontSize="12px">
                I have read and accept{" "}
                <Text
                  as="span"
                  color="brand.primary"
                  fontFamily="medium"
                  onClick={handleTNCClick}
                >
                  Terms of Service.
                </Text>
              </Text>
            </Checkbox>
          </Box>
          <Alert type="info" withIcon={false} my="1rem">
            <UnorderedList
              spacing={3}
              fontSize="12px"
              px="0.5rem"
              fontFamily="light"
            >
              <ListItem>
                For web browser wallet extension users having trouble connecting
                to a specific wallet. Please try disabling other extension
                wallets.
              </ListItem>
              <ListItem>
                For Wallet Connect users, please switch network in the wallet
                app first and then connect for best experience.
              </ListItem>
            </UnorderedList>
          </Alert>
          <SimpleGrid
            columns={2}
            gap="1rem"
            mb="1rem"
            pointerEvents={!isChecked ? "none" : "inherit"}
            opacity={isChecked ? "1" : "0.5"}
          >
            {walletMenuOptions.map((wallet: WalletMenuOption) => {
              const { key, label, icon } = wallet;

              return (
                <Flex
                  flexDir="column"
                  as={Button}
                  key={key}
                  bg="brand.textBg"
                  onClick={() => handleClick(key)}
                  width="100%"
                  outline="none"
                  h="48px"
                  _hover={{
                    background: "brand.primary",
                    color: "brand.textLight",
                  }}
                >
                  <Flex width="100%" gap="0.5rem" alignItems="center">
                    <Flex align="center" borderRadius="50%" overflow="hidden">
                      <Image
                        src={
                          isZerion(key)
                            ? walletIconMapping.zerion[colorMode]
                            : icon[colorMode]
                        }
                        alt={`${key} Logo`}
                        width={20}
                        height={20}
                      />
                    </Flex>
                    <Text
                      fontSize="12px"
                      lineHeight="16px"
                      fontFamily="light"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {label}
                    </Text>
                  </Flex>
                </Flex>
              );
            })}
          </SimpleGrid>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default WalletConnectModal;
